/**
 * Pure adapter helpers for the customer-facing WhatsApp Embedded Signup page.
 *
 * Why this file exists as a SEPARATE, dependency-free module:
 *   The live Meta Embedded Signup flow (FB.login config + the postMessage shape
 *   that Meta posts back to the page on completion) is NOT yet verified against
 *   a real run — we don't have WhatsApp Tech Provider approval yet, so we can't
 *   exercise the real SDK. To keep this safe, every part that does NOT touch the
 *   FB SDK / DOM is isolated here as pure functions and unit-tested, and the
 *   live FB calls live in the .vue and are clearly marked "VERIFY
 *   post-Tech-Provider-approval." This mirrors how the backend
 *   lib/waOnboarding/senders.js isolated the unverified Senders API.
 *
 * No DOM, no FB, no axios in this file — pure data in / data out.
 */

/**
 * Extract the onboarding token from a query string.
 * @param {string} search - e.g. '?token=abc.def' or location.search ('' if none).
 * @returns {string} the token value, or '' when absent.
 */
export function parseToken(search) {
  try {
    const params = new URLSearchParams(search || '');
    return params.get('token') || '';
  } catch (_) {
    return '';
  }
}

/**
 * Read the number and the WhatsApp name a connect link carries, for the page to SHOW.
 *
 * The backend mints `<base64url(JSON payload)>.<signature>` (conversations-service
 * lib/waOnboarding/token.js) and, since 2026-09-11, puts two display fields in the payload:
 * `n` — the number the practice must type into Facebook (the site's own Teamio number) — and
 * `d` — the WhatsApp name. A practice keeping its WhatsApp Business app number has TWO numbers,
 * and Facebook's phone step will take either; typing the app number is the one mistake that
 * can cost them the app. So the page names the right one.
 *
 * DISPLAY ONLY. The page cannot check the signature (it has no secret) and does not need to:
 * the fields are signed, so a link whose number was edited is refused at signup, and nothing
 * here decides anything. Links minted before this change have no `n`/`d` — the page falls back
 * to generic wording. Never throws.
 *
 * @param {string} token
 * @returns {{ phone: string, displayName: string }} '' for anything missing or unreadable
 */
export function readLinkDetails(token) {
  const none = { phone: '', displayName: '' };
  try {
    const payload = String(token || '').split('.')[0];
    if (!payload) return none;
    const b64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4);
    // UTF-8, not latin1: atob alone would mangle an accented practice name.
    const bytes = Uint8Array.from(atob(padded), (c) => c.charCodeAt(0));
    const data = JSON.parse(new TextDecoder().decode(bytes));
    if (!data || typeof data !== 'object') return none;
    return {
      phone: typeof data.n === 'string' ? data.n.trim() : '',
      displayName: typeof data.d === 'string' ? data.d.trim() : '',
    };
  } catch (_) {
    return none;
  }
}

/**
 * Split a stored number into what Facebook's phone step asks for: a country and the digits.
 *
 * Facebook's phone field starts with a COUNTRY list, and several countries share +1. A
 * practice in Barbados will see Barbados selected by default, while the Teamio number is a US
 * number — so the screen names the country as well as the digits. Every Teamio purchase path
 * buys US numbers (a2p-service `availablePhoneNumbers('US')`), so a +1 number here is a US
 * number. ASSUMPTION, written down: a practice's own non-US +1 number moved onto Teamio would
 * be mislabelled; that path is handled by Teamio on a call, not by this screen alone.
 *
 * @param {string} phone e.g. '+15615550123'
 * @returns {{ country: string, local: string, display: string }|null} null when unusable
 */
export function signInNumberParts(phone) {
  const raw = String(phone || '').replace(/[\s().-]/g, '');
  const us = /^\+1(\d{3})(\d{3})(\d{4})$/.exec(raw);
  if (us) {
    const local = `(${us[1]}) ${us[2]}-${us[3]}`;
    return { country: 'United States (+1)', local, display: `+1 ${local}` };
  }
  // A +1 number that is not exactly ten digits is malformed: showing it as "the number to enter"
  // would send the practice to type a number that cannot be right. Generic wording instead.
  if (raw.startsWith('+1')) return null;
  if (/^\+\d{7,15}$/.test(raw)) return { country: '', local: raw, display: raw };
  return null;
}

/**
 * Build the options object passed to FB.login() for Embedded Signup with
 * WABA sharing (the customer shares an existing/new WhatsApp Business Account
 * with us, the Tech Provider).
 *
 * Every field here has a source (2026-07-21):
 *  - `setup.solutionID` — the Twilio **Partner Solution ID**. Twilio's Tech Provider
 *    integration guide puts it HERE, as a client-side FB.login param. It is NOT a field
 *    in Meta's ES Builder config (verified 2026-07-21: no such field exists there).
 *  - `featureType: 'only_waba_sharing'` — Twilio's doc says to use it when the sender is
 *    a Twilio SMS-capable number, which is exactly our model (the practice's existing
 *    Teamio number becomes the WhatsApp sender). NOTE Twilio support (ticket 28334152 Q1)
 *    said it is "not REQUIRED"; that is weaker than "don't use it", and the doc's
 *    condition applies to us.
 *    ⚠️ CORRECTED 2026-09-11: this was believed to make the signup "no phone add, no OTP".
 *    The Mint prod pilot (2026-08-03, WHATSAPP.md) DISPROVED that: for a practice with no
 *    WhatsApp account yet — every Teamio practice — Facebook's flow always asks for a phone
 *    number and texts a code to it. `only_waba_sharing` skips that only when sharing an
 *    account that already has a number. So the practice types the Teamio number into
 *    Facebook, and the code arrives at the Teamio number. That is why the page names the
 *    number to enter (`readLinkDetails` below + WhatsAppSignInSteps.vue).
 *  - `sessionInfoVersion: 3` — required to get the WABA id back (number, per the doc).
 *  - `version: 'v4'` — from Meta's own generated snippet for OUR config.
 *
 * With `only_waba_sharing`, Meta may return NO `phone_number_id`; that is expected and
 * handled — `extractSignupResult` nulls it and the Senders body omits it.
 *
 * @param {{ configId: string, solutionId: string }} opts
 * @returns {object} the FB.login config object.
 */
export function buildFbLoginConfig({ configId, solutionId } = {}) {
  return {
    config_id: configId,
    response_type: 'code',
    override_default_response_type: true,
    extras: {
      sessionInfoVersion: 3,
      featureType: 'only_waba_sharing',
      version: 'v4',
      setup: { solutionID: solutionId },
    },
  };
}

/**
 * Pull BOTH ids out of Meta's WA_EMBEDDED_SIGNUP completion payload.
 *
 * Twilio (ticket 28334152 Q2) confirmed we pass the WABA ID **and** the Meta phone
 * number ID to the Senders API — Twilio performs the token exchange with Meta itself,
 * so we never handle an auth code or token. `waba_id` is required; `phone_number_id`
 * is surfaced as null when absent rather than failing the whole signup.
 *
 * Defensive by contract: NEVER throws on malformed/partial input — returns null.
 *
 * @param {*} messageData - the (already parsed) data object from the message event.
 * @returns {{ wabaId: string, phoneNumberId: string|null }|null}
 */
export function extractSignupResult(messageData) {
  if (!messageData || typeof messageData !== 'object') return null;
  if (messageData.type !== 'WA_EMBEDDED_SIGNUP') return null;
  if (messageData.event !== 'FINISH') return null;
  const data = messageData.data || {};
  const wabaId = data.waba_id || null;
  if (!wabaId) return null;
  return { wabaId, phoneNumberId: data.phone_number_id || null };
}

/**
 * Pull the WABA id out of Meta's WA_EMBEDDED_SIGNUP postMessage payload.
 *
 * VERIFY post-Tech-Provider-approval: the documented success payload shape is
 *   { type: 'WA_EMBEDDED_SIGNUP', event: 'FINISH', data: { waba_id, phone_number_id } }
 * but this has not been observed against a live run. This function is
 * intentionally defensive — it must NEVER throw on malformed / partial input
 * (a string, null, missing data, etc.), only return null.
 *
 * @param {*} messageData - the (already parsed) data object from the message event.
 * @returns {string|null} the waba_id on a valid FINISH payload, else null.
 */
export function extractWabaId(messageData) {
  const result = extractSignupResult(messageData);
  return result ? result.wabaId : null;
}
